/**
 * A synthetic photoplethysmographic subject.
 *
 * Used for tests, for the demo mode when no camera is available, and for the
 * performance sweeps in the operator console. It generates the same KIND of
 * signal a camera produces — a large DC level, slow baseline wander, a
 * respiratory component, a two-harmonic pulse wave and sensor noise — with the
 * pulsatile term attenuated by skin tone.
 *
 * It is deliberately NOT a model fitted to any published dataset. It exists so
 * the estimator can be exercised against a known answer.
 */

import { signalAttenuation } from './monk.js';

/** Deterministic LCG so every test and sweep is reproducible. */
export class Rng {
  constructor(seed = 12345) {
    this.state = (seed >>> 0) || 1;
  }

  next() {
    this.state = (Math.imul(this.state, 1664525) + 1013904223) & 0x7fffffff;
    return this.state / 0x7fffffff;
  }

  /** Box–Muller, one value per call (the discarded half costs less than caching). */
  gauss() {
    const u = this.next() || 1e-12;
    return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * this.next());
  }
}

export const SUBJECT_DEFAULTS = Object.freeze({
  fs: 30,
  dcLevel: 128,
  noiseSigma: 0.55,
  driftSigma: 0.004,
  driftDecay: 0.999,
  driftGain: 8,
  respHz: 0.25,
  respAmplitude: 0.35,
  secondHarmonic: 0.35,
  hrvJitter: 0.00035,
});

export class SyntheticSubject {
  /**
   * @param {object} opts
   * @param {number} opts.bpm   true heart rate
   * @param {number} opts.tone  Monk skin tone 1–10
   * @param {number} [opts.seed]
   */
  constructor({ bpm = 72, tone = 1, seed = 12345, ...overrides } = {}) {
    this.cfg = { ...SUBJECT_DEFAULTS, ...overrides };
    this.bpm = bpm;
    this.tone = tone;
    this.attenuation = signalAttenuation(tone);
    this.rng = new Rng(seed);
    this.phase = 0;
    this.drift = 0;
    this.i = 0;
  }

  /** One sample of the green-channel mean. */
  next() {
    const c = this.cfg;
    const t = this.i / c.fs;

    this.phase += this.bpm / 60 / c.fs + this.rng.gauss() * c.hrvJitter;
    const p = this.phase % 1;
    const pulse =
      this.attenuation *
      (Math.sin(2 * Math.PI * p) + c.secondHarmonic * Math.sin(4 * Math.PI * p));

    this.drift = this.drift * c.driftDecay + this.rng.gauss() * c.driftSigma;
    const respiration = c.respAmplitude * Math.sin(2 * Math.PI * c.respHz * t);
    const noise = this.rng.gauss() * c.noiseSigma;

    this.i++;
    return c.dcLevel + this.drift * c.driftGain + respiration + pulse + noise;
  }

  /** Generate `n` samples at once. */
  take(n) {
    const out = new Float64Array(n);
    for (let i = 0; i < n; i++) out[i] = this.next();
    return out;
  }
}

/** Convenience: a fresh subject's first `n` samples. */
export function generate({ bpm = 72, tone = 1, seed = 12345, samples = 1024, ...rest } = {}) {
  return new SyntheticSubject({ bpm, tone, seed, ...rest }).take(samples);
}
